import React from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Star } from "lucide-react";
import { formatJMD } from "@/lib/format";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const PurchasesSection = ({ products, onSelect }) => {
  const purchases = products.filter((p) => p.type === "purchase");

  return (
    <section id="purchases" data-testid="section-purchases-pricing" className="relative py-24 bg-white overflow-hidden">
      <div className="absolute top-40 right-0 w-80 h-80 bg-cyan-200 rounded-full cw-blob opacity-30" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-600">Bottle Sizes & Purchases</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-3">
            Choose your <span className="cw-text-gradient">perfect size.</span>
          </h2>
          <p className="mt-4 text-slate-600 text-base lg:text-lg leading-relaxed">
            Live pricing, managed by our team. Available for single cases or bulk enterprise orders — delivered across
            the island.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {purchases.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
              className={`relative rounded-3xl p-7 border transition-all flex flex-col group overflow-hidden ${
                p.popular
                  ? "bg-sky-950 border-sky-800 shadow-2xl shadow-sky-900/30"
                  : "bg-slate-50 border-slate-200 hover:shadow-xl hover:border-sky-200"
              }`}
            >
              {p.popular && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-sky-400 text-sky-950 text-xs font-bold uppercase tracking-wider">
                  <Star className="w-3 h-3 fill-sky-950" /> {p.badge || "Most Popular"}
                </div>
              )}
              <div className="-mx-7 -mt-7 mb-5 h-44 rounded-t-3xl overflow-hidden bg-sky-100">
                <img
                  src={p.image_url ? `${BACKEND_URL}${p.image_url}` : /case|pack|24|28/i.test(`${p.name} ${p.size}`) ? "/brand/case.png" : /gallon/i.test(`${p.name} ${p.size}`) ? "/brand/carboys-crop.jpg" : "/brand/bottle-crop.png"}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  data-testid={`purchase-image-${p.id}`}
                />
              </div>
              <h3 className={`font-display text-xl font-bold mt-4 ${p.popular ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
              <div className={`text-sm font-semibold mt-1 ${p.popular ? "text-sky-300" : "text-sky-600"}`}>{p.size}</div>
              <p className={`text-sm mt-3 leading-relaxed flex-1 ${p.popular ? "text-sky-100/75" : "text-slate-600"}`}>{p.description}</p>

              <div className="mt-5 flex items-end justify-between">
                <div>
                  <div className={`font-mono-cw text-3xl font-extrabold ${p.popular ? "text-white" : "text-sky-950"}`}>
                    {formatJMD(p.price_jmd)}
                  </div>
                  {p.stock_status !== "in_stock" && (
                    <span className="text-xs font-semibold text-rose-500">Out of stock</span>
                  )}
                </div>
              </div>

              <Button
                data-testid={`purchase-select-${p.id}`}
                onClick={() => onSelect(p)}
                disabled={p.stock_status !== "in_stock"}
                className={`mt-5 rounded-full h-11 font-semibold ${
                  p.popular ? "bg-sky-400 hover:bg-sky-300 text-sky-950" : "bg-sky-600 hover:bg-sky-500 text-white"
                }`}
              >
                <ShoppingCart className="w-4 h-4 mr-1.5" /> Select for Order
              </Button>
            </motion.div>
          ))}
          {purchases.length === 0 && (
            <div className="col-span-full bg-slate-50 rounded-3xl p-10 border border-dashed border-slate-300 text-slate-400 text-center">
              No bottles available yet. Check back soon.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
